// src/modules/calendar/components/MonthFlatView.jsx
// Vista de scroll infinito (virtual): renderiza ~24 semanas centradas en `date`.
// Comparte la misma lógica que ThreeWeekView.
import React from 'react';
import addDays from 'date-fns/addDays';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import format from 'date-fns/format';
import { Navigate } from 'react-big-calendar';
import es from 'date-fns/locale/es';
import { ThreeWeekEnumContext } from './ThreeWeekView';

const DAY_HEADERS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
const MIN_WEEKS_BEFORE = 12;
const MIN_WEEKS_AFTER = 12;
const BUFFER_WEEKS_AFTER_LAST_EVENT = 4;
const EVENT_ROW_HEIGHT = 19;
const CELL_HEADER_HEIGHT = 30;
const CELL_PADDING = 6;
const MIN_EVENTS_HEIGHT = 3;

const MonthFlatView = ({ date, events, onSelectEvent, eventPropGetter }) => {
    const { cache: enumCache, getAutoColor, onEventDrop, onVisibleLabel } = React.useContext(ThreeWeekEnumContext);
    const dragEventRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const weekRefs = React.useRef({});
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    // Centrar en la semana del primer día del mes del `date`
    const monthStart = startOfMonth(date);
    const targetWeekStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    // Rango dinámico
    const eventDates = events
        .filter(e => e.start)
        .map(e => (e.start instanceof Date ? e.start : new Date(e.start)))
        .filter(d => !isNaN(d));
    const earliestEvent = eventDates.length ? new Date(Math.min(...eventDates.map(d => d.getTime()))) : targetWeekStart;
    const latestEvent = eventDates.length ? new Date(Math.max(...eventDates.map(d => d.getTime()))) : targetWeekStart;
    const minStart = addDays(targetWeekStart, -7 * MIN_WEEKS_BEFORE);
    const minEnd = addDays(targetWeekStart, 7 * MIN_WEEKS_AFTER);
    const earliestStart = startOfWeek(earliestEvent < minStart ? earliestEvent : minStart, { weekStartsOn: 1 });
    const latestStart = startOfWeek(latestEvent > minEnd ? latestEvent : minEnd, { weekStartsOn: 1 });
    const firstWeekStart = earliestStart;
    const lastWeekStart = addDays(latestStart, 7 * BUFFER_WEEKS_AFTER_LAST_EVENT);
    const totalWeeksDynamic = Math.max(1, Math.round((lastWeekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1);
    const targetWeekIdx = Math.max(0, Math.round((targetWeekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)));
    const weeks = Array.from({ length: totalWeeksDynamic }, (_, i) => {
        const weekStart = addDays(firstWeekStart, i * 7);
        return Array.from({ length: 7 }, (_, di) => addDays(weekStart, di));
    });

    const getEventsForDay = (day) =>
        events
            .filter(ev => {
                const s = ev.start instanceof Date ? ev.start : new Date(ev.start);
                return isSameDay(s, day);
            })
            .sort((a, b) => {
                const as = a.start instanceof Date ? a.start : new Date(a.start);
                const bs = b.start instanceof Date ? b.start : new Date(b.start);
                return as - bs;
            });

    const allDays = weeks.flat();
    const maxEvents = Math.max(MIN_EVENTS_HEIGHT, ...allDays.map(d => getEventsForDay(d).length));
    const cellMinHeight = CELL_HEADER_HEIGHT + (maxEvents * EVENT_ROW_HEIGHT) + CELL_PADDING;

    React.useLayoutEffect(() => {
        const target = weekRefs.current[targetWeekIdx];
        if (target && containerRef.current) {
            target.scrollIntoView({ block: 'start' });
        }
    }, [date]);

    React.useEffect(() => {
        const ct = containerRef.current;
        if (!ct) return;
        let raf = null;
        const update = () => {
            const scrollTop = ct.scrollTop;
            const viewportH = ct.clientHeight;
            const centerY = scrollTop + viewportH / 2;
            let idx = targetWeekIdx;
            for (let i = 0; i < totalWeeksDynamic; i++) {
                const ref = weekRefs.current[i];
                if (!ref) continue;
                if (centerY >= ref.offsetTop && centerY < ref.offsetTop + ref.offsetHeight) {
                    idx = i;
                    break;
                }
            }
            // Mes/año del jueves de la semana central (representa el mes "dominante")
            const thursday = addDays(weeks[idx][0], 3);
            const lbl = format(thursday, 'MMMM yyyy', { locale: es });
            if (onVisibleLabel) onVisibleLabel(lbl);
        };
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = null; update(); });
        };
        ct.addEventListener('scroll', onScroll, { passive: true });
        update();
        return () => ct.removeEventListener('scroll', onScroll);
    }, [date, events, onVisibleLabel]);

    return (
        <div ref={containerRef} style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'inherit', overflow: 'auto' }}>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                borderBottom: '1px solid var(--cal-border, #dadce0)', background: 'var(--cal-bg, #fff)',
                flexShrink: 0, position: 'sticky', top: 0, zIndex: 10,
            }}>
                {DAY_HEADERS.map(d => (
                    <div key={d} style={{
                        textAlign: 'center', padding: '8px 0',
                        fontSize: '11px', fontWeight: 500, color: 'var(--cal-text-muted, #70757a)',
                        textTransform: 'uppercase', letterSpacing: '.8px',
                    }}>{d}</div>
                ))}
            </div>

            {weeks.map((week, weekIdx) => (
                <div key={weekIdx}
                    ref={el => { weekRefs.current[weekIdx] = el; }}
                    style={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                        flexShrink: 0,
                        minHeight: cellMinHeight,
                        borderBottom: '1px solid var(--cal-border, #dadce0)',
                    }}>
                    {week.map((day, dayIdx) => {
                        const isToday = isSameDay(day, new Date());
                        const isPast = day < todayMidnight;
                        const isOffMonth = !isSameMonth(day, date);
                        const dayEvents = getEventsForDay(day);
                        return (
                            <div key={dayIdx}
                                onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = 'var(--cal-hover, #e0e7ff)'; }}
                                onDragLeave={e => { e.currentTarget.style.background = isOffMonth ? 'var(--cal-bg-alt, #f8f9fa)' : (isToday ? 'var(--cal-bg-today)' : isPast ? 'var(--cal-bg-past)' : 'var(--cal-bg)'); }}
                                onDrop={e => {
                                    e.preventDefault();
                                    e.currentTarget.style.background = isOffMonth ? 'var(--cal-bg-alt, #f8f9fa)' : (isToday ? 'var(--cal-bg-today)' : isPast ? 'var(--cal-bg-past)' : 'var(--cal-bg)');
                                    if (dragEventRef.current && onEventDrop) {
                                        const ev = dragEventRef.current;
                                        const origStart = ev.start instanceof Date ? ev.start : new Date(ev.start);
                                        const origEnd = ev.end instanceof Date ? ev.end : new Date(ev.end);
                                        const duration = origEnd.getTime() - origStart.getTime();
                                        const newStart = new Date(day);
                                        newStart.setHours(origStart.getHours(), origStart.getMinutes(), 0, 0);
                                        const newEnd = new Date(newStart.getTime() + duration);
                                        onEventDrop({ event: ev, start: newStart, end: newEnd });
                                        dragEventRef.current = null;
                                    }
                                }}
                                style={{
                                    borderLeft: dayIdx > 0 ? '1px solid var(--cal-border, #dadce0)' : 'none',
                                    background: isOffMonth ? 'var(--cal-bg-alt, #f8f9fa)' : (isToday ? 'var(--cal-bg-today)' : isPast ? 'var(--cal-bg-past)' : 'var(--cal-bg)'),
                                    display: 'flex', flexDirection: 'column',
                                    overflow: 'hidden',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '4px 6px 2px', flexShrink: 0, gap: '4px' }}>
                                    {day.getDate() === 1 && (
                                        <span style={{ fontSize: '10px', color: 'var(--cal-text-muted, #70757a)', fontWeight: 500, textTransform: 'capitalize' }}>
                                            {format(day, 'MMM', { locale: es })}
                                        </span>
                                    )}
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: '24px', height: '24px', borderRadius: '50%',
                                        fontSize: '12px', fontWeight: isToday ? 700 : 500,
                                        backgroundColor: isToday ? '#1a73e8' : 'transparent',
                                        color: isToday ? '#fff' : isOffMonth ? '#b0b3b8' : isPast ? 'var(--cal-text-dim)' : 'var(--cal-text)',
                                    }}>
                                        {format(day, 'd')}
                                    </span>
                                </div>
                                <div style={{ padding: '0 2px 2px', flex: 1 }}>
                                    {dayEvents.map((ev, evIdx) => {
                                        const evStyle = eventPropGetter ? eventPropGetter(ev, ev.start, ev.end, false) : {};
                                        const evClassName = evStyle.className || '';
                                        const evStart = ev.start instanceof Date ? ev.start : new Date(ev.start);
                                        const hrs = evStart.getHours();
                                        const mins = evStart.getMinutes();
                                        const ampm = hrs >= 12 ? 'pm' : 'am';
                                        const h12 = hrs % 12 || 12;
                                        const timeStr = mins === 0 ? `${h12}${ampm}` : `${h12}:${String(mins).padStart(2, '0')}${ampm}`;
                                        const info = enumCache[ev.id];
                                        const dotColor = getAutoColor(ev, enumCache);
                                        const courseAbrev = ev.resource?.courseAbreviatura || ev.courseAbreviatura;
                                        const courseName = courseAbrev || ev.resource?.courseName || '';
                                        const asesorShort = (ev.type === 'masterclass' || ev.resource?.type === 'masterclass')
                                            ? (ev.asesorName || '').split(' ')[0] : '';
                                        let displayTitle;
                                        if (info && courseName) {
                                            displayTitle = `${info.num} ${info.type} ${courseName}${asesorShort ? ` - ${asesorShort}` : ''}`;
                                        } else if (info) {
                                            displayTitle = `${info.num} ${info.type} ${ev.title || ''}${asesorShort ? ` - ${asesorShort}` : ''}`;
                                        } else {
                                            displayTitle = ev.title || '';
                                        }
                                        return (
                                            <div key={evIdx}
                                                className={`rbc-event ${evClassName}`}
                                                draggable={ev.source !== 'google_calendar'}
                                                onDragStart={(e) => { dragEventRef.current = ev; e.dataTransfer.effectAllowed = 'move'; e.currentTarget.style.opacity = '0.4'; }}
                                                onDragEnd={(e) => { e.currentTarget.style.opacity = '1'; dragEventRef.current = null; }}
                                                onClick={(e) => { e.stopPropagation(); onSelectEvent && onSelectEvent(ev, e); }}
                                                title={`${timeStr} ${ev.title || ''} (${info ? `${info.num}/${info.total}` : ''})`}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '3px',
                                                    padding: '0 2px 0 5px', marginBottom: '1px',
                                                    borderRadius: '4px', cursor: 'pointer',
                                                    height: '18px', overflow: 'hidden',
                                                    fontSize: '11px', lineHeight: '18px',
                                                    whiteSpace: 'nowrap', fontStyle: 'normal',
                                                    ...(evStyle.style || {}),
                                                }}>
                                                <span style={{ flexShrink: 0, width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, display: 'inline-block' }} />
                                                <span style={{ fontSize: '10px', opacity: 0.85, flexShrink: 0, fontWeight: 400, color: 'var(--cal-event-time)' }}>{timeStr}</span>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500, color: 'var(--cal-event-title)' }}>{displayTitle}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

MonthFlatView.navigate = (date, action) => {
    const d = new Date(date);
    if (action === Navigate.PREVIOUS) { d.setMonth(d.getMonth() - 1); return d; }
    if (action === Navigate.NEXT) { d.setMonth(d.getMonth() + 1); return d; }
    return new Date();
};

MonthFlatView.range = (date) => {
    const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
    return { start, end: addDays(start, 41) };
};

MonthFlatView.title = (date) => format(date, 'MMMM yyyy', { locale: es });

export default MonthFlatView;
